<template>
  <Card class="seller-card">
    <template #title>
      <div class="flex justify-content-between align-items-center">
        <span>Seller {{ index + 1 }}</span>
        <Button
          v-if="canRemove"
          icon="pi pi-times"
          severity="danger"
          text
          rounded
          @click="$emit('remove')"
        />
      </div>
    </template>
    <template #content>
      <div class="grid">
        <!-- Name -->
        <div class="flex flex-column gap-1 col-12 md:col-6">
          <label :for="`sellerName${index}`">*Name</label>
          <InputText
            :id="`sellerName${index}`"
            v-model="localValue.name"
            placeholder="e.g. News Site"
          />
        </div>

        <!-- Domain -->
        <div class="flex flex-column gap-1 col-12 md:col-6">
          <label :for="`sellerDomain${index}`">*Domain</label>
          <InputText
            :id="`sellerDomain${index}`"
            v-model="localValue.domain"
            placeholder="e.g. example.com (without www)"
          />
        </div>

        <!-- Password -->
        <div class="flex flex-column gap-1 col-12 md:col-6">
          <label :for="`sellerPassword${index}`">*Password</label>
          <InputText
            :id="`sellerPassword${index}`"
            v-model="localValue.password"
            type="password"
            placeholder="Seller login password"
          />
          <small class="text-500">Required for seller login access</small>
        </div>

        <!-- Seller Type -->
        <div class="flex flex-column gap-1 col-12 md:col-6">
          <label :for="`sellerType${index}`">*Seller Type</label>
          <Dropdown
            :id="`sellerType${index}`"
            v-model="localValue.seller_type"
            :options="sellerTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Type"
          />
        </div>

        <!-- Connatix Player ID (only for Audio + Video) -->
        <div
          v-if="localValue.seller_type === 'audio_video'"
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`connatixPlayerId${index}`">Connatix Player ID</label>
          <InputText
            :id="`connatixPlayerId${index}`"
            v-model="localValue.connatix_player_id"
            placeholder="e.g. 12345-abcde (optional)"
          />
          <small class="text-500"
            >Optional: For video monetization with Connatix</small
          >
        </div>

        <!-- Connatix Share (only for Audio + Video) -->
        <div
          v-if="localValue.seller_type === 'audio_video'"
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`connatixShare${index}`"
            >Connatix Share (0.01 - 1.00)</label
          >
          <InputNumber
            :id="`connatixShare${index}`"
            v-model="localValue.connatix_player_share"
            :min="0.01"
            :max="1"
            :step="0.01"
            placeholder="e.g. 0.60"
            :minFractionDigits="2"
            :maxFractionDigits="2"
          />
          <small class="text-500"
            >Revenue share for video (default: 0.60)</small
          >
        </div>

        <!-- Content Selection -->
        <div class="col-12">
          <Divider />
          <h4 class="mt-0">Content</h4>
        </div>

        <div class="flex flex-column gap-1 col-12">
          <ContentMediaTypeSelector
            v-model="localValue.content_media_type"
            :id-suffix="index"
          />
        </div>

        <div
          class="flex flex-column gap-1 col-12"
          v-if="localValue.content_media_type === 'snippet'"
        >
          <RestrictedValidationToggle
            v-model="localValue.restricted_validation"
          />
        </div>

        <!-- Content Mode -->
        <div class="flex flex-column gap-1 col-12">
          <label>Content Source</label>
          <div class="flex gap-4">
            <div
              v-if="localValue.content_media_type !== 'snippet'"
              class="flex align-items-center"
            >
              <RadioButton
                :id="`contentModeClone${index}`"
                v-model="contentMode"
                value="clone"
                name="contentMode"
              />
              <label :for="`contentModeClone${index}`" class="ml-2"
                >Clone from existing seller</label
              >
            </div>
            <div class="flex align-items-center">
              <RadioButton
                :id="`contentModeSelect${index}`"
                v-model="contentMode"
                value="select"
                name="contentMode"
              />
              <label :for="`contentModeSelect${index}`" class="ml-2"
                >Select content type</label
              >
            </div>
          </div>
        </div>

        <!-- Clone from Seller -->
        <div
          v-if="contentMode === 'clone'"
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`cloneSeller${index}`">Clone Content From</label>
          <Dropdown
            :id="`cloneSeller${index}`"
            v-model="localValue.clone_seller_id"
            :options="existingSellers"
            optionLabel="name"
            optionValue="id"
            placeholder="Select Seller"
            filter
          >
            <template #value="slotProps">
              <span v-if="slotProps.value">
                {{
                  existingSellers.find((s) => s.id === slotProps.value)?.name ||
                  slotProps.value
                }}
                <span class="text-500 text-sm">
                  (ID:
                  {{
                    existingSellers.find((s) => s.id === slotProps.value)?.id
                  }})
                </span>
              </span>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div
                class="flex justify-content-between align-items-center w-full gap-3"
              >
                <span>{{ slotProps.option.name }}</span>
                <span class="text-500 text-sm"
                  >ID: {{ slotProps.option.id }}</span
                >
              </div>
            </template>
          </Dropdown>
          <small class="text-500"
            >Content settings will be copied from selected seller</small
          >
        </div>

        <!-- Content Type Selection -->
        <div
          v-if="contentMode === 'select'"
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`contentType${index}`">*Content Type</label>
          <Dropdown
            :id="`contentType${index}`"
            v-model="localValue.content_type"
            :options="contentTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Content Type"
          />
        </div>

        <!-- Shows Selection -->
        <div
          v-if="contentMode === 'select' && localValue.content_type === 'shows'"
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`shows${index}`">*Select Shows</label>
          <MultiSelect
            :id="`shows${index}`"
            v-model="localValue.show_ids_content"
            :options="showsOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Shows"
            display="chip"
            filter
            class="w-full shows-multiselect"
            panelClass="shows-multiselect-panel"
            appendTo="self"
          >
            <template #option="slotProps">
              <div class="flex align-items-center gap-2">
                <span>{{ slotProps.option.label }}</span>
                <span v-if="slotProps.option.fadeOutSec" class="fade-badge"
                  >fade-out {{ slotProps.option.fadeOutSec }}s</span
                >
              </div>
            </template>
          </MultiSelect>
        </div>

        <template
          v-if="contentMode === 'select' && localValue.content_type === 'shows'"
        >
          <DisplayOrderSelector
            v-if="
              localValue.content_media_type === 'snippet' &&
              localValue.show_ids_content?.length
            "
            v-model="displayOrder"
            class="col-12 mt-2"
          />
          <div
            v-if="
              localValue.content_media_type === 'snippet' &&
              localValue.show_ids_content?.length
            "
            class="flex flex-column gap-3 mt-2 col-12"
          >
            <draggable
              v-model="localValue.show_ids_content"
              :item-key="(id) => id"
              handle=".drag-handle"
              animation="150"
              tag="div"
              class="flex flex-column gap-3"
              :disabled="displayOrder.shuffle"
            >
              <template #item="{ element: showId, index }">
                <div v-if="snippetShowConfigs[showId]" class="show-order-card">
                  <div class="show-order-header">
                    <i
                      v-if="!displayOrder.shuffle"
                      class="pi pi-bars drag-handle flex-none"
                    ></i>
                    <span class="show-dot flex-none"></span>
                    <span class="show-title flex-1">{{
                      showsOptions.find((s) => s.value === showId)?.label
                    }}</span>
                    <span
                      v-if="!displayOrder.shuffle"
                      class="show-position-badge flex-none"
                      >{{ index + 1 }}</span
                    >
                  </div>
                  <div class="show-order-fields">
                    <div class="show-field">
                      <label class="text-xs text-500"
                        >Snippets per ep (max
                        {{
                          showsOptions.find((s) => s.value === showId)
                            ?.snippetsPerEpisodeConfig
                        }})</label
                      >
                      <InputNumber
                        v-model="
                          snippetShowConfigs[showId].snippets_per_episode
                        "
                        :min="1"
                        :max="
                          showsOptions.find((s) => s.value === showId)
                            ?.snippetsPerEpisodeConfig || 1
                        "
                        :class="{ 'p-invalid': perEpisodeExceedsTotal(showId) }"
                        class="w-full"
                      />
                    </div>
                    <div class="show-field">
                      <label class="text-xs text-500"
                        >Total snippets (available
                        {{ availableSnippetsFor(showId) }})</label
                      >
                      <InputNumber
                        v-model="snippetShowConfigs[showId].snippets_count"
                        :min="1"
                        :max="
                          Math.min(
                            MAX_TOTAL_SNIPPETS,
                            Math.max(availableSnippetsFor(showId), 1)
                          )
                        "
                        class="w-full"
                      />
                    </div>
                  </div>
                  <small v-if="perEpisodeExceedsTotal(showId)" class="p-error"
                    >Snippets per ep can't exceed total snippets</small
                  >
                  <small class="block text-500">
                    {{ getShowOption(showId)?.enabledSnippetCount }}
                    enabled ·
                    {{ getShowOption(showId)?.disabledSnippetCount }}
                    disabled snippet(s) generated for this show
                  </small>
                </div>
              </template>
            </draggable>

            <small
              :class="totalSnippetsRequested > 25 ? 'p-error' : 'text-500'"
            >
              Total snippets requested: {{ totalSnippetsRequested }} / 25
            </small>
          </div>
        </template>

        <!-- Contract Type -->
        <div class="flex flex-column gap-1 col-12 md:col-6">
          <label :for="`contractType${index}`">*Contract Type</label>
          <Dropdown
            :id="`contractType${index}`"
            v-model="localValue.contract_type"
            :options="contractTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Contract"
            @change="handleContractTypeChange"
          />
        </div>

        <!-- Contract Value -->
        <div class="flex flex-column gap-1 col-12 md:col-6">
          <label :for="`contractValue${index}`">*Contract Value</label>
          <div v-if="localValue.contract_type === 'cpm'">
            <InputNumber
              :id="`contractValue${index}`"
              v-model="localValue.contract_value"
              mode="currency"
              currency="USD"
              :min="0"
              :max="100"
              placeholder="CPM Rate"
            />
          </div>
          <div v-else-if="localValue.contract_type === 'revshare'">
            <div class="flex align-items-center gap-2">
              <Slider
                v-model="localValue.contract_value"
                :min="5"
                :max="75"
                :step="5"
                class="flex-1"
              />
              <span class="font-bold">{{ localValue.contract_value }}%</span>
            </div>
          </div>
        </div>

        <!-- Categories Selection -->
        <div
          v-if="
            contentMode === 'select' && localValue.content_type === 'categories'
          "
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`categories${index}`">*Select Categories</label>
          <MultiSelect
            :id="`categories${index}`"
            v-model="localValue.categories"
            :options="categoriesOptions"
            placeholder="Select Categories"
            display="chip"
            filter
            class="w-full"
          />
        </div>

        <!-- Playlists Selection -->
        <div
          v-if="
            contentMode === 'select' && localValue.content_type === 'playlist'
          "
          class="flex flex-column gap-1 col-12 md:col-6"
        >
          <label :for="`playlists${index}`">*Select Playlist</label>
          <Dropdown
            :id="`playlists${index}`"
            v-model="localValue.playlists"
            :options="playlistsOptions"
            optionLabel="name"
            optionValue="uuid"
            placeholder="Select Playlist"
            filter
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup>
import {
  nextTick,
  ref,
  watch,
  onMounted,
  computed,
  defineProps,
  defineEmits,
} from "vue";
import axios from "axios";
import { API_BASE_URL, getContentTypeOptions } from "@/consts";
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import Button from "primevue/button";
import Slider from "primevue/slider";
import Divider from "primevue/divider";
import RadioButton from "primevue/radiobutton";
import ContentMediaTypeSelector from "../ContentMediaTypeSelector.vue";
import RestrictedValidationToggle from "../RestrictedValidationToggle.vue";
import DisplayOrderSelector from "../DisplayOrderSelector.vue";
import draggable from "vuedraggable";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  canRemove: {
    type: Boolean,
    default: true,
  },
  groupData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "remove"]);

// Filter seller type based on BOTH video_monetization_enabled AND connatix_reporting_enabled
const sellerTypeOptions = computed(() => {
  const options = [{ label: "Audio Only", value: "audio" }];

  // Only show "Audio + Video" if BOTH flags are enabled
  const videoEnabled = props.groupData?.video_monetization_enabled === true;
  const connatixEnabled = props.groupData?.connatix_reporting_enabled === true;

  if (videoEnabled && connatixEnabled) {
    options.push({ label: "Audio + Video", value: "audio_video" });
  }

  return options;
});

const contractTypeOptions = [
  { label: "Revenue Share", value: "revshare" },
  { label: "Per Load (CPM)", value: "cpm" },
];

const localValue = ref({ ...props.modelValue });
const isSyncingFromProps = ref(false);

// Content selection
const contentMode = ref("select"); // cl clone | select
const contentTypeOptions = computed(() =>
  getContentTypeOptions(
    [
      { label: "Shows", value: "shows" },
      { label: "Categories", value: "categories" },
      { label: "Playlists", value: "playlist" },
    ],
    localValue.value.content_media_type === "snippet"
  )
);

const existingSellers = ref([]);
const showsOptions = ref([]);
const categoriesOptions = ref([]);
const playlistsOptions = ref([]);

const fetchPodcastShows = async () => {
  const token = localStorage.getItem("adminToken");
  const showsResponse = await axios.get(`${API_BASE_URL}/podcast-shows`, {
    headers: { Authorization: `Bearer ${token}` },
    params:
      localValue.value.content_media_type === "snippet"
        ? { snippetsOnly: true }
        : {},
  });
  const rawShows =
    localValue.value.content_media_type === "snippet"
      ? (showsResponse.data || []).filter(
          (show) => (show.snippet_count || 0) > 0
        )
      : showsResponse.data || [];

  showsOptions.value = rawShows.map((show) => {
    const generationConfig = show.snippets_generation_config
      ? typeof show.snippets_generation_config === "string"
        ? JSON.parse(show.snippets_generation_config)
        : show.snippets_generation_config
      : null;

    return {
      label: show.show_title || show.title,
      value: show.show_id || show.id,
      snippet_count: show.snippet_count || 0,
      enabledSnippetCount: show.enabled_snippet_count || 0,
      disabledSnippetCount: show.disabled_snippet_count || 0,
      snippetsPerEpisodeConfig: generationConfig?.snippets_per_episode || 1,
      fadeOutSec: generationConfig?.fade_out_sec || 0,
    };
  });
};

const getShowOption = (showId) =>
  showsOptions.value.find((s) => s.value === showId);

const availableSnippetsFor = (showId) => {
  const show = getShowOption(showId);
  if (!show) return MAX_TOTAL_SNIPPETS;
  return localValue.value.restricted_validation
    ? show.enabledSnippetCount
    : show.enabledSnippetCount + show.disabledSnippetCount;
};

const MAX_TOTAL_SNIPPETS = 25;
const snippetShowConfigs = ref({});
const displayOrder = ref({ shuffle: false });

const totalSnippetsRequested = computed(() =>
  (localValue.value.show_ids_content || []).reduce((sum, showId) => {
    const config = snippetShowConfigs.value[showId];
    return sum + (Number(config?.snippets_count) || 0);
  }, 0)
);

const perEpisodeExceedsTotal = (showId) => {
  const config = snippetShowConfigs.value[showId];
  return (
    !!config &&
    Number(config.snippets_per_episode) > Number(config.snippets_count)
  );
};

watch(
  () => localValue.value.show_ids_content,
  (newShows) => {
    const showById = new Map(showsOptions.value.map((s) => [s.value, s]));
    const newConfigs = {};
    for (const showId of newShows || []) {
      newConfigs[showId] = snippetShowConfigs.value[showId] || {
        snippets_count: 1,
        snippets_per_episode:
          showById.get(showId)?.snippetsPerEpisodeConfig || 1,
      };
    }
    snippetShowConfigs.value = newConfigs;
  },
  { deep: true }
);

watch(totalSnippetsRequested, (count) => {
  localValue.value.selected_shows_snippet_count = count;
});

watch(
  [snippetShowConfigs, displayOrder],
  ([configs, order]) => {
    localValue.value.content_allocation_config = {
      shows: (localValue.value.show_ids_content || []).map((showId) => ({
        show_id: showId,
        snippets_count: configs[showId]?.snippets_count,
        snippets_per_episode: configs[showId]?.snippets_per_episode,
      })),
      display_order: order,
    };
  },
  { deep: true }
);

// Fetch content data
const fetchContentData = async () => {
  try {
    const token = localStorage.getItem("adminToken");

    // Fetch existing sellers
    const sellersResponse = await axios.get(`${API_BASE_URL}/sellers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    existingSellers.value = sellersResponse.data || [];

    // Fetch podcast shows
    await fetchPodcastShows();

    // Fetch categories
    const categoriesResponse = await axios.get(`${API_BASE_URL}/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    categoriesOptions.value = categoriesResponse.data || [];

    // Fetch playlists
    const partnerId = props.groupData?.partner_id;
    if (partnerId) {
      const playlistsResponse = await axios.get(`${API_BASE_URL}/playlists`, {
        headers: { Authorization: `Bearer ${token}`, partnerid: partnerId },
      });
      playlistsOptions.value = (playlistsResponse.data.playlists || []).filter(
        (item) => item.status === "ready"
      );
    } else {
      playlistsOptions.value = [];
    }
  } catch (error) {
    // Silent fail - content data is not critical
  }
};

onMounted(() => {
  fetchContentData();
});

// Watch for clone seller selection - auto-populate content
watch(
  () => localValue.value.clone_seller_id,
  async (newSellerId) => {
    if (!newSellerId || contentMode.value !== "clone") return;

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${API_BASE_URL}/seller/${newSellerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.seller) {
        const seller = response.data.seller;

        // Clone content settings
        localValue.value.content_media_type =
          seller.content_media_type || "podcast";
        localValue.value.restricted_validation = !!seller.restricted_validation;
        localValue.value.content_type = seller.content_type || "shows";
        localValue.value.show_ids_content = seller.show_ids_content || [];
        localValue.value.categories =
          typeof seller.categories === "string"
            ? JSON.parse(seller.categories)
            : seller.categories || [];
        localValue.value.playlists = seller.playlist_id || null;

        // Clone Connatix settings if seller type is audio_video
        if (seller.seller_type === "audio_video") {
          localValue.value.connatix_player_id = seller.connatix_player_id || "";
          localValue.value.connatix_player_share =
            seller.connatix_player_share || 0.6;
        }
      }
    } catch (error) {
      // Silent fail - cloning is optional
    }
  }
);

watch(
  () => localValue.value.content_media_type,
  (newMediaType) => {
    if (
      !contentTypeOptions.value.some(
        (item) => item.value === localValue.value.content_type
      )
    ) {
      localValue.value.content_type = contentTypeOptions.value[0].value;
    }
    localValue.value.show_ids_content = [];
    fetchPodcastShows();

    if (newMediaType === "snippet" && contentMode.value === "clone") {
      contentMode.value = "select";
    }
  }
);

// Watch for changes and emit
watch(
  localValue,
  (newValue) => {
    if (isSyncingFromProps.value) return;
    emit("update:modelValue", newValue);
  },
  { deep: true }
);

// Watch props changes
watch(
  () => props.modelValue,
  (newValue) => {
    isSyncingFromProps.value = true;
    localValue.value = { ...newValue };
    nextTick(() => {
      isSyncingFromProps.value = false;
    });
  },
  { deep: true }
);

const handleContractTypeChange = () => {
  // Reset value when contract type changes
  if (localValue.value.contract_type === "cpm") {
    localValue.value.contract_value = 2.5;
  } else {
    localValue.value.contract_value = 50;
  }
};
</script>

<style scoped>
.seller-card {
  border: 1px solid var(--surface-border);
  transition: all 0.3s ease;
}

.drag-handle {
  cursor: grab;
  color: var(--text-color-secondary, #9ca3af);
  font-size: 0.9rem;
}

.drag-handle:active {
  cursor: grabbing;
}

.show-order-card {
  border: 1px solid var(--surface-border);
  border-left: 4px solid var(--primary-color);
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.show-order-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.show-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color);
}

.show-title {
  font-weight: 600;
}

.show-position-badge {
  background: color-mix(in srgb, var(--primary-color) 12%, transparent);
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 600;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.show-order-fields {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.show-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 8rem;
}

.show-order-card :deep(.p-inputnumber-input) {
  width: 100%;
  min-width: 0;
}

.seller-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

:deep(.shows-multiselect) {
  position: relative;
}

:deep(.shows-multiselect-panel) {
  max-width: 96vw;
  width: min(18rem, 96vw);
}

:deep(.shows-multiselect-panel .p-multiselect-item) {
  white-space: normal;
  word-break: break-word;
}
.fade-badge {
  font-size: 0.7rem;
  line-height: 1;
  padding: 0.2rem 0.45rem;
  border-radius: 0.75rem;
  color: var(--primary-color);
  background: rgba(255, 90, 87, 0.12);
  white-space: nowrap;
}
</style>
